import { mapText } from "./map-i18n.js";
import { zoneDisplayNameFromCatalog } from "./map-zone-catalog.js";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  return String(value ?? "").trim();
}

function rgbTriplet(value) {
  if (!Array.isArray(value) || value.length !== 3) {
    return null;
  }
  return value.every(
    (channel) => Number.isInteger(channel) && channel >= 0 && channel <= 255,
  )
    ? [...value]
    : null;
}

function nonNegativeInteger(value) {
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function normalizeAuthoredAt(value) {
  const kind = trimString(value?.kind);
  const authoredAt = trimString(value?.value);
  return (kind === "date" || kind === "instant") && authoredAt
    ? { kind, value: authoredAt }
    : null;
}

function normalizeProvenance(value) {
  const kind = trimString(value?.kind);
  if (kind === "leak_known") {
    return { kind };
  }
  const author = trimString(value?.author);
  return (kind === "review_known" || kind === "guess") && author
    ? { kind, author }
    : null;
}

function normalizeEvidence(value) {
  const label = trimString(value?.label);
  const byteLength = nonNegativeInteger(value?.byteLength);
  const sha256 = trimString(value?.sha256);
  if (!label || byteLength == null || !/^[a-f0-9]{64}$/i.test(sha256)) {
    return null;
  }
  return { label, byteLength, sha256: sha256.toLowerCase() };
}

function normalizeRevision(value) {
  const revisionOrdinal = nonNegativeInteger(value?.revisionOrdinal);
  const revisionId = trimString(value?.revisionId);
  const resultRgb = rgbTriplet(value?.resultRgb);
  const purpose = trimString(value?.purpose);
  const provenance = normalizeProvenance(value?.provenance);
  const authoredAt = normalizeAuthoredAt(value?.authoredAt);
  const timestampSource = trimString(value?.timestampSource);
  const evidence = normalizeEvidence(value?.evidence);
  if (
    revisionOrdinal == null ||
    !revisionId ||
    !resultRgb ||
    !purpose ||
    !provenance ||
    !authoredAt ||
    !timestampSource ||
    !evidence
  ) {
    return null;
  }
  return {
    revisionOrdinal,
    revisionId,
    resultRgb,
    purpose,
    provenance,
    authoredAt,
    timestampSource,
    evidence,
  };
}

export function normalizeZoneMaskLineage(value) {
  if (!isPlainObject(value) || value.schemaVersion !== 1) {
    return null;
  }
  const sourceRevisionId = trimString(value.sourceRevisionId);
  const sourceRgbFieldSha256 = trimString(value.sourceRgbFieldSha256);
  const x = nonNegativeInteger(value.x);
  const y = nonNegativeInteger(value.y);
  const lineageId = nonNegativeInteger(value.lineageId);
  const currentRgb = rgbTriplet(value.currentRgb);
  const revisions = Array.isArray(value.revisions)
    ? value.revisions.map(normalizeRevision)
    : [];
  if (
    !sourceRevisionId ||
    !/^[a-f0-9]{64}$/i.test(sourceRgbFieldSha256) ||
    x == null ||
    y == null ||
    !lineageId ||
    !currentRgb ||
    !revisions.length ||
    revisions.some((revision) => revision == null) ||
    revisions[0].revisionOrdinal !== 0 ||
    revisions.some(
      (revision, index) =>
        index > 0 &&
        revision.revisionOrdinal <= revisions[index - 1].revisionOrdinal,
    ) ||
    revisions.at(-1).resultRgb.some((channel, index) => channel !== currentRgb[index])
  ) {
    return null;
  }
  return {
    schemaVersion: 1,
    sourceRevisionId,
    sourceRgbFieldSha256: sourceRgbFieldSha256.toLowerCase(),
    x,
    y,
    lineageId,
    currentRgb,
    revisions,
  };
}

export function buildZoneMaskLineageSection(selection, visible, zoneCatalog = []) {
  if (visible !== true) {
    return null;
  }
  const lineage = normalizeZoneMaskLineage(selection?.zoneMaskLineage);
  if (!lineage) {
    return null;
  }
  const currentZoneName = zoneDisplayNameFromCatalog(zoneCatalog, lineage.currentRgb);
  return {
    id: "zone-mask-lineage",
    kind: "zone-mask-lineage",
    title: mapText("info.pane.lineage"),
    ...lineage,
    ...(currentZoneName ? { currentZoneName } : {}),
    revisions: lineage.revisions.map((revision) => {
      const resultZoneName = zoneDisplayNameFromCatalog(
        zoneCatalog,
        revision.resultRgb,
      );
      return resultZoneName ? { ...revision, resultZoneName } : revision;
    }),
  };
}

function provenancePresentation(revision) {
  if (revision.provenance.kind === "guess") {
    return {
      author: revision.provenance.author,
      confidence: mapText("info.lineage.guess"),
    };
  }
  if (revision.provenance.kind === "review_known") {
    return {
      author: revision.provenance.author,
      confidence: mapText("info.lineage.known"),
    };
  }
  return {
    author: mapText("info.lineage.leak"),
    confidence: mapText("info.lineage.known"),
  };
}

function readableAuthoredAt(authoredAt) {
  return authoredAt.kind === "instant"
    ? authoredAt.value.replace("T", " ").replace(/Z$/, " UTC")
    : authoredAt.value;
}

function shortSha256(value) {
  return `${value.slice(0, 12)}…`;
}

function normalizedLineageSection(value) {
  const lineage = normalizeZoneMaskLineage(value);
  if (!lineage) {
    return null;
  }
  const currentZoneName = trimString(value?.currentZoneName);
  return {
    ...lineage,
    ...(currentZoneName ? { currentZoneName } : {}),
    revisions: lineage.revisions.map((revision, index) => {
      const resultZoneName = trimString(
        value?.revisions?.[index]?.resultZoneName,
      );
      return resultZoneName ? { ...revision, resultZoneName } : revision;
    }),
  };
}

function rgbCalloutMarkup(label, rgb, zoneName, escapeHtml) {
  return `
    <div class="mt-1">
      <p class="m-0 flex items-center gap-1.5 text-[11px] text-base-content/50">
        <span
          class="fishymap-layer-fact-swatch"
          style="--fishymap-layer-fact-rgb:${escapeHtml(rgb.join(" "))};"
          aria-hidden="true"
        ></span>
        <span>${escapeHtml(label)}</span>
      </p>
      ${
        zoneName
          ? `<p class="m-0 mt-0.5 pl-5 text-[11px] font-medium text-base-content/65">${escapeHtml(zoneName)}</p>`
          : ""
      }
    </div>
  `;
}

function revisionMarkup(revision, index, escapeHtml) {
  const provenance = provenancePresentation(revision);
  return `
    <li class="list-row items-start gap-3 border-b border-base-300/70 px-3 py-3 last:border-b-0">
      <div class="pt-0.5 text-xs font-semibold tabular-nums text-base-content/45" aria-hidden="true">
        ${String(index + 1).padStart(2, "0")}
      </div>
      <article class="min-w-0">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <time class="text-xs font-semibold text-base-content/75">${escapeHtml(readableAuthoredAt(revision.authoredAt))}</time>
          <span class="badge badge-ghost badge-sm">${escapeHtml(provenance.confidence)}</span>
        </div>
        <p class="m-0 mt-1 text-sm font-semibold text-base-content">${escapeHtml(revision.purpose)}</p>
        <p class="m-0 mt-1 text-xs text-base-content/65">${escapeHtml(provenance.author)}</p>
        ${rgbCalloutMarkup(
          mapText("info.lineage.result_rgb", { rgb: revision.resultRgb.join(",") }),
          revision.resultRgb,
          revision.resultZoneName,
          escapeHtml,
        )}
        <p class="m-0 mt-2 break-words text-[11px] text-base-content/50">
          ${escapeHtml(mapText("info.lineage.evidence"))}: ${escapeHtml(revision.evidence.label)}
          · <span tabindex="0" data-fishy-tooltip="${escapeHtml(revision.evidence.sha256)}" aria-label="${escapeHtml(revision.evidence.sha256)}">${escapeHtml(shortSha256(revision.evidence.sha256))}</span>
        </p>
        <p class="m-0 mt-1 break-words text-[11px] text-base-content/45">${escapeHtml(mapText("info.lineage.timestamp_source"))}: ${escapeHtml(revision.timestampSource)}</p>
      </article>
    </li>
  `;
}

export function zoneMaskLineageSectionMarkup(section, escapeHtml = String) {
  const lineage = normalizedLineageSection(section);
  if (!lineage) {
    return "";
  }
  return `
    <section class="space-y-3">
      <div class="rounded-box border border-base-300/70 bg-base-200/70 px-3 py-2">
        <p class="m-0 text-xs font-semibold text-base-content/75">${escapeHtml(mapText("info.lineage.coordinate", { x: lineage.x, y: lineage.y }))}</p>
        ${rgbCalloutMarkup(
          mapText("info.lineage.current_rgb", { rgb: lineage.currentRgb.join(",") }),
          lineage.currentRgb,
          lineage.currentZoneName,
          escapeHtml,
        )}
      </div>
      <ol class="list overflow-hidden rounded-box border border-base-300/70 bg-base-100/80">
        ${lineage.revisions
          .map((revision, index) =>
            revisionMarkup(revision, index, escapeHtml),
          )
          .join("")}
      </ol>
      <p class="m-0 break-all text-[10px] text-base-content/40">${escapeHtml(mapText("info.lineage.source_revision", { revision: lineage.sourceRevisionId }))}</p>
    </section>
  `;
}
